// src/components/Leads.tsx
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next'; 
import { Phone, MessageSquare, Mail, Search, Calendar, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from "sonner";
import { getLeads, updateLeadStatus, getLeadCounts, Lead } from "../../../services/lead.service";
import { Seo } from "../../components/Seo";

function LeadsSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="h-8 w-60 bg-gray-200 animate-pulse rounded-lg mb-2" /><div className="h-4 w-32 bg-gray-200 animate-pulse rounded-lg" /></div>
        <div className="h-10 w-28 bg-gray-200 animate-pulse rounded-xl" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-3">
            <div className="h-7 w-10 bg-gray-200 animate-pulse mx-auto mb-3" />
            <div className="h-4 w-16 bg-gray-200 animate-pulse mx-auto" />
          </div>
        ))}
      </div>
      <div className="mb-5"><div className="h-11 w-full bg-gray-200 animate-pulse rounded-xl" /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="bg-white border border-[#E5E7EB] rounded-2xl p-4">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-200 animate-pulse rounded-xl" />
                <div><div className="h-4 w-36 bg-gray-200 animate-pulse mb-2" /><div className="h-3 w-20 bg-gray-200 animate-pulse" /></div>
              </div>
              <div className="h-7 w-20 bg-gray-200 animate-pulse rounded-lg" />
            </div>
            <div className="h-4 w-56 bg-gray-200 animate-pulse mb-3" />
            <div className="flex gap-2 items-center mb-3">
              <div className="h-6 w-24 bg-gray-200 animate-pulse rounded-lg" />
              <div className="h-4 w-32 bg-gray-200 animate-pulse" />
            </div>
            <div className="h-14 w-full bg-gray-200 animate-pulse rounded-xl mb-4" />
            <div className="flex gap-2">
              {[1,2,3].map(j => <div key={j} className="flex-1 h-10 bg-gray-200 animate-pulse rounded-xl" />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Leads() {
  const { t } = useTranslation('auth'); 
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, hot: 0, contacted: 0, converted: 0, cold: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updating, setUpdating] = useState<string | null>(null);

  // ✅ Status config with translations
  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; class: string }> = {
      new: { label: t('leads.status.new'), class: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
      contacted: { label: t('leads.status.contacted'), class: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
      hot: { label: t('leads.status.hot'), class: 'bg-[#EF4444]/10 text-[#EF4444]' },
      cold: { label: t('leads.status.cold'), class: 'bg-[#E5E7EB] text-[#717182]' },
      converted: { label: t('leads.status.converted'), class: 'bg-[#22C55E]/10 text-[#22C55E]' },
    };
    return configs[status] || configs.new;
  };

  // ✅ Lead source icons with translations
  const getTypeIcon = (source: string) => {
    const icons: Record<string, any> = {
      'Phone Call': Phone,
      'Message': MessageSquare,
      'WhatsApp': MessageSquare,
      'Email': Mail,
    };
    return icons[source] || MessageSquare;
  };

  const getTranslatedSource = (source: string) => {
    const map: Record<string, string> = {
      'Phone Call': t('leads.leadSource.phoneCall'),
      'Message': t('leads.leadSource.message'),
      'WhatsApp': t('leads.leadSource.whatsapp'),
      'Email': t('leads.leadSource.email'),
    };
    return map[source] || source;
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params: any = { page, pageSize: 10 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const res = await getLeads(params);
      if (res.success) {
        setLeads(res.data);
        if (res.pagination) {
          setTotalPages(res.pagination.total_pages);
        }
      } else {
        toast.error(res.message || t('leads.messages.loadFailed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('leads.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const res = await getLeadCounts();
      if (res.success) setCounts(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchLeads();
    fetchCounts();
  }, [statusFilter, search, page]);

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      setUpdating(leadId);
      const res = await updateLeadStatus(leadId, newStatus);
      if (res.success) {
        toast.success(t('messages.updateSuccess'));
        fetchLeads();
        fetchCounts();
      } else {
        toast.error(res.message || t('leads.messages.updateFailed'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('leads.messages.updateFailed'));
    } finally {
      setUpdating(null);
    }
  };

  // Stats configuration with translations
  const statsConfig = [
    { label: t('leads.stats.total'), value: counts.total, status: 'all' },
    { label: t('leads.stats.new'), value: counts.new, status: 'new' },
    { label: t('leads.stats.hot'), value: counts.hot, status: 'hot' },
    { label: t('leads.stats.contacted'), value: counts.contacted, status: 'contacted' },
    { label: t('leads.stats.converted'), value: counts.converted, status: 'converted' },
  ];

  if (loading && leads.length === 0) {
    return <LeadsSkeleton />;
  }

  return (
    <>
    <Seo
      title="Leads | Morrocars"
      description="Manage and track your vehicle leads on Morrocars. View buyer enquiries, contact leads, update lead status and connect with potential buyers."
    />
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('leads.title')}</h1>
          <p className="text-[#717182]">{counts.total} {t('leads.totalLeads')}</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] bg-white rounded-xl hover:border-[#1E6DFF] transition-colors text-sm text-[#2D323A]">
          <Calendar className="w-4 h-4" /> {t('leads.actions.export')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {statsConfig.map(s => (
          <button key={s.status} onClick={() => setStatusFilter(s.status)}
            className={`p-3 rounded-xl text-center transition-colors ${statusFilter === s.status ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#2D323A] hover:border-[#1E6DFF]'}`}>
            <p className="text-xl">{s.value}</p>
            <p className="text-xs">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
          <input value={search} onChange={e => setSearch(e.target.value)} 
            placeholder={t('leads.search.placeholder')} 
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {leads.length === 0 ? (
          <div className="col-span-2 text-center py-10 text-[#717182]">{t('leads.noLeads')}</div>
        ) : (
          leads.map(lead => {
            const sc = getStatusConfig(lead.Status);
            const TypeIcon = getTypeIcon(lead.LeadSource);
            const vehicle = lead.Vehicle;
            const seller = lead.Seller;
            return (
              <div key={lead.id} onClick={() => setSelected(lead.id === selected ? null : lead.id)}
                className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all ${selected === lead.id ? 'border-[#1E6DFF] shadow-md' : 'border-[#E5E7EB] hover:border-[#BFC2C7]'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center text-[#1E6DFF]">
                      {lead.BuyerName[0]}
                    </div>
                    <div>
                      <p className="text-sm text-[#2D323A]">{lead.BuyerName}</p>
                      <p className="text-xs text-[#717182]">{new Date(lead.CreatedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-lg ${sc.class}`}>{sc.label}</span>
                </div>
                <p className="text-sm text-[#717182] mb-2">
                  {t('leads.interestedIn')} <span className="text-[#2D323A]">{vehicle?.Title || 'Vehicle'}</span>
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F5F7FA] rounded-lg text-[#717182]">
                    <TypeIcon className="w-3 h-3" /> {getTranslatedSource(lead.LeadSource)}
                  </span>
                  <span className="text-xs text-[#717182]">{lead.BuyerPhone}</span>
                </div>
                {lead.Message && (
                  <p className="text-xs text-[#717182] bg-[#F5F7FA] rounded-xl p-2 mb-3">{lead.Message}</p>
                )}
                {selected === lead.id && (
                  <div className="space-y-2 pt-2 border-t border-[#E5E7EB]">
                    <div className="flex gap-2">
                      <a href={`tel:${lead.BuyerPhone}`} className="flex-1 py-2 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-xs flex items-center justify-center gap-1">
                        <Phone className="w-3 h-3" /> {t('leads.actions.call')}
                      </a>
                      <button className="flex-1 py-2 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors text-xs flex items-center justify-center gap-1">
                        <MessageSquare className="w-3 h-3" /> {t('leads.actions.message')}
                      </button>
                      <a href={`https://wa.me/${lead.BuyerPhone.replace(/\s/g, '')}`} target="_blank" rel="noreferrer" className="flex-1 py-2 bg-[#25D366] text-white rounded-xl hover:bg-[#20bd5a] transition-colors text-xs flex items-center justify-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {t('leads.actions.whatsapp')}
                      </a>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {['new', 'contacted', 'hot', 'cold', 'converted'].map(status => (
                        <button
                          key={status}
                          onClick={(e) => { e.stopPropagation(); handleStatusChange(lead.id, status); }}
                          disabled={updating === lead.id}
                          className={`px-3 py-1 text-xs rounded-lg border transition-colors ${lead.Status === status ? 'bg-[#1E6DFF] text-white border-[#1E6DFF]' : 'bg-white text-[#2D323A] border-[#E5E7EB] hover:border-[#1E6DFF]'} disabled:opacity-50`}
                        >
                          {updating === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t(`leads.status.${status}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} className={`w-10 h-10 rounded-xl text-sm transition-colors ${p === page ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182] hover:border-[#1E6DFF]'}`}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
